/*
 * <fp-loop-video> - silent, looping, autoplaying product video with NO player UI.
 *
 * Why a custom element instead of Dawn's <deferred-media>: Dawn shows a poster + play
 * button, then swaps in a <video controls>. We want a hero loop that just plays.
 * No play button anywhere: if autoplay is blocked the poster simply stays.
 *
 * Performance rules baked in:
 *  - The poster <img> is the only thing in the initial payload (it is the LCP element).
 *    No video bytes are requested until the element is on screen; it then starts at once.
 *  - One rendition is picked from Shopify's own transcodes (480/720/1080 mp4): the smallest
 *    one that covers slot width x devicePixelRatio. Retina desktop and iPhone -> 1080,
 *    small non-retina screens -> 720 / 480.
 *  - Plays only while visible; pauses off-screen and in background tabs (battery + data).
 *  - Honors prefers-reduced-motion and Save-Data / 2G: poster only, no download.
 */
(function () {
  if (window.customElements && customElements.get('fp-loop-video')) return;

  function conn() {
    return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  }

  class FpLoopVideo extends HTMLElement {
    connectedCallback() {
      if (this._fpInit) return;
      this._fpInit = true;
      this.video = this.querySelector('.fp-loop-video__el');
      if (!this.video) return;

      var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      var c = conn();
      var saver = c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''));
      if (reduce || saver) { this.classList.add('is-poster-only'); return; }

      try { this.sources = JSON.parse(this.getAttribute('data-sources') || '[]'); } catch (e) { this.sources = []; }
      if (!this.sources.length) return;

      this.started = false;
      this.wantPlay = false;

      /* Pause in background tabs, resume when back and still visible */
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) this.pause(); else if (this.visible) this.play();
      }.bind(this));

      if (!('IntersectionObserver' in window)) { this.start(); return; }
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          this.visible = e.isIntersecting && e.intersectionRatio >= 0.15;
          if (this.visible) {
            if (!this.started) this.start();
            else this.play();
          } else {
            this.pause();
          }
        }.bind(this));
      }.bind(this), { threshold: [0, 0.15, 0.5], rootMargin: '120px 0px' });
      this.io.observe(this);
    }

    pickSource() {
      var w = (this.clientWidth || 360) * (window.devicePixelRatio || 1);
      var c = conn();
      /* On 3G-class links never go above the smallest rendition */
      var slow = c && /3g/.test(c.effectiveType || '');
      var list = this.sources.slice().sort(function (a, b) { return a.width - b.width; });
      if (slow) return list[0];
      for (var i = 0; i < list.length; i++) if (list[i].width >= w) return list[i];
      return list[list.length - 1];
    }

    /* Ordered ladder starting at the rendition that fits; on error fall through to larger ones */
    start() {
      if (this.started) return;
      this.started = true;
      var v = this.video;
      var list = this.sources.slice().sort(function (a, b) { return a.width - b.width; });
      var first = this.pickSource();
      var queue = list.filter(function (s) { return s.width >= first.width; });
      var self = this;
      var load = function () {
        var src = queue.shift();
        if (!src) return;
        v.src = src.url;
        v.load();
        if (self.visible !== false) self.play();
      };
      v.addEventListener('error', load);
      v.addEventListener('playing', function () { self.classList.add('is-playing'); });
      /* loop attribute handles this; the listener is a safety net for old Safari */
      v.addEventListener('ended', function () { v.currentTime = 0; self.play(); });
      v.preload = 'auto';
      this.wantPlay = true;
      load();
    }

    play() {
      var v = this.video;
      if (!v || !this.started) return;
      this.wantPlay = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* blocked: poster stays, no UI */ });
    }

    pause() {
      if (this.video && this.started && !this.video.paused) this.video.pause();
    }
  }

  customElements.define('fp-loop-video', FpLoopVideo);
})();
